import ReactGA from "react-ga4";

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/* --------------------------------------------------------------------------
 * GA4 Consent State
 * -------------------------------------------------------------------------- */
const hasAnalyticsConsent = () => {
  try {
    if (typeof window === "undefined") {
      return false;
    }

    const saved = localStorage.getItem("ninetyone_cookie_consent");
    if (!saved) {
      return false;
    }

    const consent = JSON.parse(saved);
    return consent?.analytics === true;
  } catch (error) {
    console.error("Unable to read analytics consent:", error);
    return false;
  }
};

/* --------------------------------------------------------------------------
 * Safe gtag helper
 * -------------------------------------------------------------------------- */
const gtag = (...args) => {
  if (
    typeof window !== "undefined" &&
    typeof window.gtag === "function" &&
    hasAnalyticsConsent()
  ) {
    window.gtag(...args);
  }
};

/* --------------------------------------------------------------------------
 * Initialize GA4
 * -------------------------------------------------------------------------- */
export const initGA = () => {
  if (typeof window === "undefined") {
    return;
  }

  const trackingId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!trackingId) {
    console.warn("GA4 Measurement ID is missing.");
    return;
  }

  if (!hasAnalyticsConsent()) {
    return;
  }

  // Re-enable in case the user previously withdrew consent.
  // This must come BEFORE the "already initialized" check below,
  // otherwise re-accepting would return early and stay disabled.
  window[`ga-disable-${trackingId}`] = false;

  if (window.__GA4_INITIALIZED__) {
    return;
  }

  ReactGA.initialize(trackingId, {
    gtagOptions: { send_page_view: false },
    gaOptions: {
      currency: "KES",
    },
  });

  window.__GA4_INITIALIZED__ = true;
};

/* --------------------------------------------------------------------------
 * Track Page View
 * -------------------------------------------------------------------------- */
export const trackPageView = (path, title) => {
  if (!hasAnalyticsConsent()) {
    return;
  }

  initGA();

  if (typeof window === "undefined") {
    return;
  }

  gtag("event", "page_view", {
    page_location: window.location.origin + path,
    page_path: path,
    page_title: title || document.title,
    send_to: MEASUREMENT_ID,
  });
};

/* --------------------------------------------------------------------------
 * Generic Event
 * -------------------------------------------------------------------------- */
export const trackEvent = (eventName, params = {}) => {
  if (!hasAnalyticsConsent()) {
    return;
  }

  initGA();
  gtag("event", eventName, {
    send_to: MEASUREMENT_ID,
    ...params,
  });
};

/* --------------------------------------------------------------------------
 * E-COMMERCE EVENTS
 * -------------------------------------------------------------------------- */
export const trackViewItem = (product) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "view_item", {
    currency: product.currency || "KES",
    value: product.price,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        item_variant: product.variant,
        price: product.price,
        quantity: 1,
      },
    ],
  });
};

// disable ga
export const disableGA = () => {
  if (typeof window === "undefined") return;

  const trackingId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!trackingId) return;

  // Tells gtag.js to stop sending hits for this property
  window[`ga-disable-${trackingId}`] = true;

  // Remove GA cookies (_ga and _ga_<container-id>)
  const expired = "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  const hostname = window.location.hostname;

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (name === "_ga" || name.startsWith("_ga_")) {
      document.cookie = `${name}${expired}`;
      document.cookie = `${name}${expired}; domain=${hostname}`;
      document.cookie = `${name}${expired}; domain=.${hostname}`;
    }
  });
};

export const trackAddToCart = (product, quantity = 1) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "add_to_cart", {
    currency: product.currency || "KES",
    value: product.price * quantity,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        item_variant: product.variant,
        price: product.price,
        quantity,
      },
    ],
  });
};

export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "remove_from_cart", {
    currency: product.currency || "KES",
    value: product.price * quantity,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        price: product.price,
        quantity,
      },
    ],
  });
};

export const trackBeginCheckout = (cartItems, coupon = null) => {
  if (!hasAnalyticsConsent()) return;

  try {
    initGA();
    const value = cartItems.reduce(
      (sum, item) => sum + (item.basePrice || 0) * (item.quantity || 1),
      0,
    );

    gtag("event", "begin_checkout", {
      currency: "KES",
      value,
      coupon: coupon || undefined,
      items: cartItems.map((item) => ({
        item_id: String(item.productId || item.sku),
        item_name: item.name,
        item_brand: item.brand,
        item_category: item.category || item.slug,
        price: item.basePrice,
        quantity: item.quantity || 1,
        item_variant: item.size || item.color?.name || undefined,
      })),
    });
  } catch (error) {
    console.error("GA4 begin_checkout failed:", error);
  }
};

export const trackAddShippingInfo = (
  cartItems,
  shippingTier,
  coupon = null,
) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  const value = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  gtag("event", "add_shipping_info", {
    currency: "KES",
    value,
    coupon,
    shipping_tier: shippingTier,
    items: cartItems.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity,
    })),
  });
};

export const trackAddPaymentInfo = (cartItems, paymentType, coupon = null) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  const value = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  gtag("event", "add_payment_info", {
    currency: "KES",
    value,
    coupon,
    payment_type: paymentType,
    items: cartItems.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity,
    })),
  });
};

export const trackPurchase = (order) => {
  if (!hasAnalyticsConsent()) return;

  try {
    initGA();
    gtag("event", "purchase", {
      transaction_id: order.orderNo,
      value: order.totalPrice,
      shipping: order.shippingPrice,
      currency: "KES",
      coupon: order.promoCode || undefined,
      items:
        order.cart?.map((item) => ({
          item_id: String(item.productId || item.sku),
          item_name: item.name,
          price: item.basePrice,
          quantity: item.quantity,
          item_variant: item.size || item.color?.name,
        })) || [],
    });
  } catch (error) {
    console.error("GA4 trackPurchase failed:", error);
  }
};

export const trackViewCart = (cartItems) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  const value = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  gtag("event", "view_cart", {
    currency: "KES",
    value,
    items: cartItems.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity,
    })),
  });
};

/* --------------------------------------------------------------------------
 * BUSINESS KPI EVENTS
 * -------------------------------------------------------------------------- */
export const trackSearch = (searchTerm) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "search", {
    search_term: searchTerm,
  });
};

export const trackSignUp = (method = "email") => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "sign_up", {
    method,
  });
};

export const trackLogin = (method = "email") => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "login", {
    method,
  });
};

export const trackShare = (method, contentType, itemId) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "share", {
    method,
    content_type: contentType,
    item_id: itemId,
  });
};

export const trackGenerateLead = (value, currency = "KES") => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "generate_lead", {
    value,
    currency,
  });
};

export const trackRefund = (transactionId, value, items) => {
  if (!hasAnalyticsConsent()) return;

  initGA();
  gtag("event", "refund", {
    transaction_id: transactionId,
    value,
    currency: "KES",
    items,
  });
};
